import { ImageBox } from '../components/BoxedImage';
import { ComplianceStatus, OosDetectionState, RogItemStatus } from '../types';

/**
 * Check if two boxes overlap with a given threshold using IoU (Intersection over Union)
 * @param box1 - First box as [x1, y1, x2, y2]
 * @param box2 - Second box as [x1, y1, x2, y2]
 * @param threshold - Minimum overlap ratio (0-1) to consider boxes as overlapping
 * @returns true if boxes overlap more than the threshold
 */
export function areBoxesOverlapping(
  box1: number[],
  box2: number[],
  threshold: number = 0.4
): boolean {
  if (!box1 || !box2 || box1.length !== 4 || box2.length !== 4) {
    return false;
  }

  const [x1_1, y1_1, x2_1, y2_1] = box1;
  const [x1_2, y1_2, x2_2, y2_2] = box2;

  // Calculate intersection
  const intersectionX1 = Math.max(x1_1, x1_2);
  const intersectionY1 = Math.max(y1_1, y1_2);
  const intersectionX2 = Math.min(x2_1, x2_2);
  const intersectionY2 = Math.min(y2_1, y2_2);

  // If no intersection
  if (intersectionX1 >= intersectionX2 || intersectionY1 >= intersectionY2) {
    return false;
  }

  const intersectionArea = (intersectionX2 - intersectionX1) * (intersectionY2 - intersectionY1);
  const box1Area = (x2_1 - x1_1) * (y2_1 - y1_1);
  const box2Area = (x2_2 - x1_2) * (y2_2 - y1_2);

  // Calculate IoU
  const unionArea = box1Area + box2Area - intersectionArea;
  const iou = intersectionArea / unionArea;

  return iou >= threshold;
}

type Box = {
  rogItemStatus?: RogItemStatus;
  rogItemId: number | null;
  pogItemId: string | number;
  osaStatus?: OosDetectionState;
  complianceStatus: ComplianceStatus;
  shape: any;
  productImageUri?: string;
  rogPositionVector?: {
    mod: number;
    shelf: number;
    position: number;
    tagName: string;
    modCoverageId: number;
  };
  pogPositionVector?: {
    mod: number;
    shelf: number;
    position: number;
    tagName: string;
    modCoverageId: number;
  };
};

type LayerConfig = { key: string; bboxColor: string };

export function getPlanogramComplianceActions(
  planogramComplianceData: { boxes?: Box[] } | null | undefined,
  enabledPlanogramComplianceLayers: Set<string>,
  ACTION_LAYER_CONFIG: LayerConfig[],
  selectedIlItemIds: Set<string | number>,
  selectionType?: 'pog' | 'realogram',
  originalClickedItemId?: number,
  enabledMods?: Set<string>,
  allTagNames?: string[]
): ImageBox[] {
  if (!planogramComplianceData || !Array.isArray(planogramComplianceData.boxes)) return [];

  let actions: any[] = [];

  ACTION_LAYER_CONFIG.forEach((layerConfig) => {
    if (enabledPlanogramComplianceLayers.has(layerConfig.key)) {
      const filteredBoxes = planogramComplianceData.boxes!.filter((box) => {
        if (enabledMods) {
          const tagName = box.pogPositionVector?.tagName as string;
          const isKnownMod = allTagNames ? allTagNames.includes(tagName) : true;
          if (isKnownMod && !enabledMods.has(tagName)) {
            return false;
          }
        }

        const matchesLayerType = (() => {
          switch (layerConfig.key) {
            case RogItemStatus.CORRECT:
            case RogItemStatus.MOVE:
            case RogItemStatus.OUT:
            case RogItemStatus.REMOVE:
              return box.rogItemStatus === layerConfig.key;
            default:
              return false;
          }
        })();

        if (!matchesLayerType) return false;

        if (selectedIlItemIds.size > 0 || originalClickedItemId) {
          if (selectionType === 'pog') {
            // Planogram click: Show realogram items with matching position
            const clickedItemId = originalClickedItemId || Array.from(selectedIlItemIds)[0];
            const selectedBox = planogramComplianceData.boxes!.find(
              (b: Box) => b.pogItemId === clickedItemId
            );

            if (selectedBox && selectedBox.pogPositionVector) {
              // Filter to show realogram items where rogPositionVector.position == selectedBox.pogPositionVector.position
              return (
                box.rogPositionVector &&
                box.rogPositionVector.position === selectedBox.pogPositionVector.position &&
                box.rogPositionVector.tagName === selectedBox.pogPositionVector.tagName &&
                box.rogPositionVector.shelf === selectedBox.pogPositionVector.shelf
              );
            }
            return false;
          } else {
            // Realogram click: Show only the specific item by rogItemId
            const primarySelectedId = Array.from(selectedIlItemIds)[0];
            return box.rogItemId === primarySelectedId;
          }
        }

        return true;
      });

      filteredBoxes.forEach((box) => {
        let color = layerConfig.bboxColor;

        let image = null;
        // Don't show product images on realogram bounding boxes

        actions.push({
          shape: box.shape,
          color,
          strokeWidth: 10,
          item: box,
          image,
        });
      });
    }
  });

  return actions;
}
